import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useBadgeChecker } from '../hooks/useBadgeChecker';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function AddMeasurement() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [formData, setFormData] = useState({
    measurement_date: new Date().toISOString().split('T')[0],
    standing_height: '',
    sitting_height: '',
    weight: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { checkBadges } = useBadgeChecker(childId);

  const loadChild = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}`);
      setChild(response.data);
    } catch (error) {
      toast.error('Failed to load child data');
      console.error(error);
    }
  }, [childId]);

  useEffect(() => {
    loadChild();
  }, [loadChild]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation — prevent obvious data-entry errors (e.g. 279cm height)
    const h = parseFloat(formData.standing_height);
    const w = parseFloat(formData.weight);
    const sh = formData.sitting_height ? parseFloat(formData.sitting_height) : null;
    if (!h || h < 30 || h > 230) {
      toast.error('Standing height must be between 30 and 230 cm');
      return;
    }
    if (!w || w < 2 || w > 250) {
      toast.error('Weight must be between 2 and 250 kg');
      return;
    }
    if (sh !== null && (sh < 20 || sh > 150)) {
      toast.error('Sitting height must be between 20 and 150 cm');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        measurement_date: new Date(formData.measurement_date).toISOString(),
        standing_height: h,
        sitting_height: sh,
        weight: w
      };

      await axios.post(`${API}/children/${childId}/measurements`, payload);
      toast.success('Measurement recorded successfully!');
      
      // Check for any new badges earned
      await checkBadges();
      
      navigate(`/child/${childId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record measurement');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!child) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile
          </Button>
          <h1 className="text-4xl font-bold text-white uppercase tracking-tight" data-testid="add-measurement-title">
            Add Measurement
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            {child.first_name} {child.last_name}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-measurement-form">
            <div>
              <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 block">
                Measurement Date *
              </Label>
              <Input
                data-testid="measurement-date-input"
                type="date"
                required
                value={formData.measurement_date}
                onChange={(e) => setFormData({ ...formData, measurement_date: e.target.value })}
                className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] text-white font-mono"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 block">
                  Standing Height (cm) *
                </Label>
                <Input
                  data-testid="standing-height-input"
                  type="number"
                  step="0.1"
                  min="30"
                  max="230"
                  required
                  value={formData.standing_height}
                  onChange={(e) => setFormData({ ...formData, standing_height: e.target.value })}
                  className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] text-white font-mono"
                  placeholder="e.g., 120.5"
                />
                <p className="text-xs text-zinc-600 mt-1">Must be 30–230 cm</p>
              </div>

              <div>
                <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 block">
                  Sitting Height (cm)
                </Label>
                <Input
                  data-testid="sitting-height-input"
                  type="number"
                  step="0.1"
                  value={formData.sitting_height}
                  onChange={(e) => setFormData({ ...formData, sitting_height: e.target.value })}
                  className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] text-white font-mono"
                  placeholder="e.g., 65.0"
                />
                <p className="text-xs text-zinc-600 mt-1">Required for PHV calculation</p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 block">
                Weight (kg) *
              </Label>
              <Input
                data-testid="weight-input"
                type="number"
                step="0.1"
                required
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] text-white font-mono"
                placeholder="e.g., 25.5"
              />
            </div>

            <div className="border-t border-zinc-800 pt-6">
              <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/20 p-4 text-sm text-[#02b5ff] font-mono">
                <strong className="block mb-2 text-[#02b5ff]">Note on Accuracy:</strong>
                Measurement precision is critical for accurate predictions. Ensure all measurements are taken using calibrated equipment and proper technique.
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                data-testid="cancel-button"
                type="button"
                onClick={() => navigate(`/child/${childId}`)}
                className="h-12 px-8 bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white font-bold uppercase tracking-wider text-sm"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-measurement-button"
                type="submit"
                disabled={submitting}
                className="h-12 px-8 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-sm flex-1 hover:shadow-[0_0_30px_rgba(37,99,235,0.4)] active:scale-95 transition-all"
              >
                <Save className="mr-2 h-5 w-5" />
                {submitting ? 'Saving...' : 'Save Measurement'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}